import { validateCNPJ, validateCPF } from 'validations-br'
import { z } from 'zod'
import { cleanDocument } from '~/helpers/documentNumber'
import { ConfirmPasswordSchema, PASSWORDS_MATCH, PasswordSchema } from '../auth/password'
import { isValidPhoneNumber } from '../shared/phone'
import { required } from '../shared/required'

/*
 * Formulários do cliente: dados PF e PJ (perfil e checkout), conclusão de cadastro com senha e troca de senha logado.
 * O cadastro (schemas/auth/authFormSchemas.ts) estende os dados PF e PJ daqui com senha e termos; a regra de senha é a
 * de schemas/auth/password.ts. Mensagens herdadas do legado, sem mudança.
 */

// =========================================================================
// SECTION 1: CAMPOS
// =========================================================================

const OccupationSchema = z
    .string(required('Deve informar sua área de atuação', 'Deve ser uma string'))
    .nullable()
    .refine((val) => val !== null && val.trim() !== '', {
        message: 'Deve informar sua área de atuação',
    })
    // prefault: o padrão passa pela validação, como o default do zod 3 (o default do zod 4 pula a validação).
    .prefault('')

// =========================================================================
// SECTION 2: DADOS DO CLIENTE
// =========================================================================

/** Pessoa física, sem senha (editar perfil, completar dados no checkout). */
export const CustomerPFFormSchema = z.object({
    firstName: z
        .string(required('Deve informar um nome'))
        .min(3, 'Deve conter no minímo 3 caracteres')
        .max(50, 'Deve conter no máximo 50 caracteres'),
    lastName: z
        .string(required('Deve informar um sobrenome'))
        .min(3, 'Deve conter no minímo 3 caracteres')
        .max(50, 'Deve conter no máximo 50 caracteres'),
    cpf: z
        .string(required('Deve informar um CPF', 'Deve ser uma string'))
        .nullable()
        .refine((value) => value !== null && value.trim() !== '', {
            message: 'Deve informar um CPF',
        })
        .refine((value) => value === null || validateCPF(value), {
            message: 'CPF inválido',
        }),
    phone: z.string(required('Deve informar um telefone')).refine(isValidPhoneNumber, {
        message: 'Número de telefone inválido',
    }),
    email: z.string(required('Deve informar um e-mail')).email('Deve informar um email válido'),
    occupation: OccupationSchema,
})

/** Pessoa jurídica, sem senha (editar perfil). CNPJ numérico ou alfanumérico. */
export const CustomerPJFormSchema = z.object({
    corporateName: z
        .string(required('Informe um nome válido'))
        .min(3, 'Deve conter no minímo 3 caracteres')
        .max(50, 'Deve conter no máximo 50 caracteres'),
    cnpj: z
        .string(required('Informe seu CNPJ'))
        // CNPJ alfanumérico: o validations-br (1.7+) valida em maiúsculas.
        .refine((value) => validateCNPJ(cleanDocument(value)), {
            message: 'CNPJ inválido',
        }),
    inscricaoMunicipal: z
        .string(required('Informe seu número de inscrição municipal'))
        .min(1, 'Informe seu número de inscrição municipal'),
    inscricaoEstadual: z
        .string(required('Informe seu número de inscrição estadual'))
        .min(1, 'Informe seu número de inscrição estadual'),
    occupation: OccupationSchema,
    companyPhone: z
        .string(required('Deve informar um telefone'))
        .min(10, { message: 'Número de telefone inválido' })
        .max(18, { message: 'Número de telefone inválido' }),
    email: z
        .string(required('Deve informar um email'))
        .email('Deve informar um email válido')
        .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Por favor, insira um email válido'),
})

/**
 * CNPJ no campo do documento dos formulários de completar cadastro (`cpf`), para o cliente PJ. Mesma regra do perfil PJ
 * (`CustomerPJFormSchema.cnpj`): numérico ou alfanumérico.
 */
export const CompanyDocumentSchema = z
    .string(required('Deve informar um CNPJ', 'Deve ser uma string'))
    .nullable()
    .refine((value) => value !== null && value.trim() !== '', { message: 'Deve informar um CNPJ' })
    .refine((value) => value === null || value.trim() === '' || validateCNPJ(cleanDocument(value)), {
        message: 'CNPJ inválido',
    })

/**
 * Completar o cadastro do cliente PJ (EditUserModal, `customer/update-company-customer`): o documento é o CNPJ e o
 * nome é a razão social do cadastro, só exibida (dividida em nome e sobrenome, sem a regra de tamanho do PF: a razão
 * social de uma palavra é válida). Sem senha: o PJ sempre tem senha (cadastro da loja), e a rota não a recebe.
 */
export const CustomerPJCompleteFormSchema = CustomerPFFormSchema.extend({
    firstName: z.string(),
    lastName: z.string(),
    cpf: CompanyDocumentSchema,
})

/**
 * Pessoa física com senha: o cliente sem cadastro completo (sem nome, vindo do login sem conta do carrinho) completa os
 * dados e cria a senha (EditUserModal, EditCustomerFormCheckout).
 */
export const CustomerPFWithPasswordFormSchema = CustomerPFFormSchema.extend({
    password: PasswordSchema,
    confirmPassword: ConfirmPasswordSchema,
}).refine((data) => data.confirmPassword === data.password, PASSWORDS_MATCH)

// =========================================================================
// SECTION 3: TROCA DE SENHA LOGADO
// =========================================================================

/** /perfil/alterar-senha (`PUT customer/change-password`). */
export const ChangePasswordFormSchema = z
    .object({
        currentPassword: z.string(required('Deve informar a senha atual')),
        newPassword: PasswordSchema,
        newPasswordConfirm: z.string(required('Confirme a nova senha')),
    })
    .refine((data) => data.newPasswordConfirm === data.newPassword, {
        message: 'As senhas não são iguais',
        path: ['newPasswordConfirm'],
    })

export type ICustomerPFForm = z.infer<typeof CustomerPFFormSchema>
export type ICustomerPJForm = z.infer<typeof CustomerPJFormSchema>
export type ICustomerPFWithPasswordForm = z.infer<typeof CustomerPFWithPasswordFormSchema>
export type ICustomerPJCompleteForm = z.infer<typeof CustomerPJCompleteFormSchema>
export type IChangePasswordForm = z.infer<typeof ChangePasswordFormSchema>
